import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { verifyEmail as verifyEmailService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Check, Loader2, AlertCircle, Mail } from "lucide-react";
import { Logo1895 } from "@/components/Logo1895";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (token) {
      doVerifyEmail();
    } else {
      setStatus("error");
      setMessage("Invalid verification link.");
    }
  }, [token]);

  const doVerifyEmail = async () => {
    try {
      const res = await verifyEmailService(token);
      setStatus("success");
      setMessage(res.message);
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Verification failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        <div className="mb-8">
          <Logo1895 variant="full" className="mx-auto" width={150} />
        </div>

        <div className="bg-[#121110] border border-[#2a2826] rounded-2xl p-8">
          {status === "verifying" && (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Verifying Email</h1>
              <p className="text-gray-400">Please wait...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Email Verified!</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              <Button
                onClick={() => navigate("/")}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Verification Failed</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate("/login")}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Go to Login
                </Button>
                <p className="text-xs text-gray-500">
                  Need a new verification link? Login and request one from your profile.
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
